import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, Search, MapPin, Phone, Mail,
  ChevronRight, Loader2, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import type { OnsiteClient } from '../../../api/src/types/onsite-types.js';

interface ClientForm {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  primary_contact_name: string;
  primary_contact_phone: string;
  primary_contact_email: string;
  notes: string;
}

const EMPTY_FORM: ClientForm = {
  name: '', address: '', city: '', state: '', zip: '',
  phone: '', email: '', primary_contact_name: '',
  primary_contact_phone: '', primary_contact_email: '', notes: '',
};

export default function ClientsPage() {
  const { orgId } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ClientForm>(EMPTY_FORM);

  const { data, isLoading } = useQuery({
    queryKey: ['clients', orgId, search],
    queryFn: () => onsiteApiFetch<{ clients: OnsiteClient[] }>(
      `/api/v1/onsite/clients${search ? `?q=${encodeURIComponent(search)}` : ''}`
    ),
    enabled: !!orgId,
  });

  const createClient = useMutation({
    mutationFn: (payload: Partial<OnsiteClient>) =>
      onsiteApiFetch<{ client: OnsiteClient }>('/api/v1/onsite/clients', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    await createClient.mutateAsync({
      name: form.name,
      address: form.address,
      city: form.city || undefined,
      state: form.state || undefined,
      zip: form.zip || undefined,
      phone: form.phone || undefined,
      email: form.email || undefined,
      primary_contact_name: form.primary_contact_name || undefined,
      primary_contact_phone: form.primary_contact_phone || undefined,
      primary_contact_email: form.primary_contact_email || undefined,
      notes: form.notes || undefined,
    });
  }

  const clients = data?.clients ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-xl font-bold text-foreground">Clients</h1>
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add Client
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search clients..."
            className="w-full rounded-lg border border-border bg-secondary py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="border-b border-border bg-card/50 p-4">
          <form onSubmit={handleSubmit} className="space-y-3 max-w-2xl">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-semibold text-foreground">New Client</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Company Name *</label>
                <input
                  value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required placeholder="Acme Corp" className="input"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Email</label>
                <input
                  type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="info@acme.com" className="input"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Address</label>
              <input
                value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                placeholder="123 Main St" className="input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">City</label>
                <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="input" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">State</label>
                <input value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="input" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">ZIP</label>
                <input value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} className="input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Phone</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="input" />
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Primary Contact</label>
                <input value={form.primary_contact_name} onChange={e => setForm(f => ({ ...f, primary_contact_name: e.target.value }))} placeholder="Name" className="input" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-1">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={createClient.isPending || !form.name.trim()} className="btn-primary">
                {createClient.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Create
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Client list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <Building2 className="h-12 w-12" />
            <p className="text-lg font-medium">No clients yet</p>
            <p className="text-sm">Add your first client to start managing sites and jobs.</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-2 flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" /> Add Client
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {clients.map(client => (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{client.name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {client.address && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {client.address}
                      </span>
                    )}
                    {client.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {client.phone}
                      </span>
                    )}
                    {client.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {client.email}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
